import { useSelector } from 'react-redux';
import CheckboxTree from './CheckboxTree';

function POI_SELECTOR({ get_value, selected_poi_types = { tipe_1: [], tipe_2: [], tipe_3: [] } }) {

  // global props
  // const { map_object, geo_layer_list } = useSelector((state) => state.layer);
  const insight_reducer = useSelector((state) => state.insight_reducer);
  const { list_tipe_1, list_tipe_2, list_tipe_3 } = insight_reducer || {};

  const convertToTreeStructure = (list_tipe_1, list_tipe_2, list_tipe_3) => {
    // Helper untuk generate ID (dengan cache)
    const idCache = new Map();
    const generateId = (prefix, label) => {
      const key = `${prefix}|||${label}`;
      if (idCache.has(key)) return idCache.get(key);

      const id = prefix
        ? `${prefix}-${label.replace(/\s+/g, '_')}`
        : label.replace(/\s+/g, '_');

      idCache.set(key, id);
      return id;
    };

    // Lookup tables
    const tipe2Map = new Map();
    list_tipe_2.forEach(t2 => {
      const key = t2.TIPE_1;
      if (!tipe2Map.has(key)) tipe2Map.set(key, []);
      tipe2Map.get(key).push(t2);
    });

    const tipe3Map = new Map();
    list_tipe_3.forEach(t3 => {
      const key = `${t3.TIPE_1}|||${t3.TIPE_2}`;
      if (!tipe3Map.has(key)) tipe3Map.set(key, []);
      tipe3Map.get(key).push(t3);
    });

    // Build tree
    return list_tipe_1.map(tipe1 => {
      const tipe1Id = generateId('', tipe1.TIPE_1);
      const tipe1Node = {
        id: tipe1Id,
        label: tipe1.TIPE_1,
        count: tipe1.count,
        color: tipe1.color,
        children: []
      };

      const relatedTipe2 = tipe2Map.get(tipe1.TIPE_1) || [];

      tipe1Node.children = relatedTipe2.map(tipe2 => {
        const tipe2Id = generateId(tipe1Id, tipe2.TIPE_2);
        const tipe2Node = {
          id: tipe2Id,
          label: tipe2.TIPE_2,
          count: tipe2.count,
          children: []
        };

        const key = `${tipe1.TIPE_1}|||${tipe2.TIPE_2}`;
        const relatedTipe3 = tipe3Map.get(key) || [];

        tipe2Node.children = relatedTipe3.map(tipe3 => ({
          id: generateId(tipe2Id, tipe3.TIPE_3),
          label: tipe3.TIPE_3,
          count: tipe3.count,
          code: `${tipe3.CODE_TIPE_1}-${tipe3.CODE_TIPE_2}-${tipe3.CODE_TIPE_3}`,
          lastMid: tipe3.LAST_MID
        }));

        if (tipe2Node.children.length === 0) delete tipe2Node.children;
        return tipe2Node;
      });

      if (tipe1Node.children.length === 0) delete tipe1Node.children;
      return tipe1Node;
    });
  }

  const data = convertToTreeStructure(list_tipe_1, list_tipe_2, list_tipe_3);

  const summarize_poi = (selectedIds, treeData) => {
    const result = {
      tipe_1: [],
      tipe_2: [],
      tipe_3: []
    };

    // Convert selectedIds jadi Set untuk lookup O(1)
    const selectedSet = new Set(selectedIds);

    // Helper function untuk cek apakah semua children terpilih
    const areAllChildrenSelected = (node) => {
      if (!node.children || node.children.length === 0) {
        // Leaf node, cek apakah dia terpilih
        return selectedSet.has(node.id);
      }

      // Cek semua children
      return node.children.every(child => areAllChildrenSelected(child));
    };

    // Helper function untuk collect ids yang belum diwakilkan parent
    const collectUnrepresented = (nodes, level, parentRepresented = false) => {
      nodes.forEach(node => {
        const allChildrenSelected = areAllChildrenSelected(node);

        if (parentRepresented) {
          // Parent sudah mewakili, skip semua
          return;
        }

        if (allChildrenSelected) {
          // Semua children terpilih, cukup ambil node ini
          result[`tipe_${level}`].push(node.id);
          // Jangan proses children karena sudah diwakilkan
        } else {
          // Ada children yang tidak terpilih
          if (node.children && node.children.length > 0) {
            // Proses children
            collectUnrepresented(node.children, level + 1, false);
          } else {
            // Leaf node yang terpilih tapi tidak semua siblings-nya terpilih
            if (selectedSet.has(node.id)) {
              result[`tipe_${level}`].push(node.id);
            }
          }
        }
      });
    };

    collectUnrepresented(treeData, 1);

    return result;
  }

  const expand_poi = (compactSelection, treeData) => {
    const result = [];

    // Convert compact selection jadi Set untuk lookup O(1)
    const tipe1Set = new Set(compactSelection.tipe_1 || []);
    const tipe2Set = new Set(compactSelection.tipe_2 || []);
    const tipe3Set = new Set(compactSelection.tipe_3 || []);

    // Helper function untuk collect semua IDs (termasuk parent) dari sebuah node
    const collectAllIds = (node) => {
      const ids = [node.id]; // Tambahkan ID node ini

      if (node.children && node.children.length > 0) {
        // Recursively collect dari children
        node.children.forEach(child => {
          ids.push(...collectAllIds(child));
        });
      }

      return ids;
    };

    // Traverse tree
    const traverse = (nodes, level, parentSelected = false) => {
      nodes.forEach(node => {
        let isSelected = false;

        // Cek apakah node ini terpilih di levelnya
        if (level === 1 && tipe1Set.has(node.id)) {
          isSelected = true;
        } else if (level === 2 && tipe2Set.has(node.id)) {
          isSelected = true;
        } else if (level === 3 && tipe3Set.has(node.id)) {
          isSelected = true;
        }

        if (parentSelected || isSelected) {
          // Parent atau node ini terpilih, collect semua IDs termasuk parent
          result.push(...collectAllIds(node));
        } else {
          // Lanjut ke children
          if (node.children && node.children.length > 0) {
            traverse(node.children, level + 1, false);
          }
        }
      });
    };

    traverse(treeData, 1);

    // Remove duplicates (kalau ada)
    return [...new Set(result)];
  }

  return (
    <>
      {
        (list_tipe_1?.length > 0 &&
          list_tipe_2?.length > 0 &&
          list_tipe_3?.length > 0) ?
          <CheckboxTree
            data={data}
            get_value={(selectedIds) => get_value(summarize_poi(selectedIds, data))}
            init_data={expand_poi(selected_poi_types, data)}
          /> :
          <div className='center_perfect'>
            <div className="spinner" id="spinner" style={{ width: "100px", height: "100px" }} />
          </div>
      }
    </>
  )
}

export default POI_SELECTOR